import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SignIn, ArrowLeft, GoogleLogo } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(useLocation().state?.error || "");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-border rounded-sm px-4 py-3.5 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" data-testid="admin-login-back-link" className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300 mb-10">
          <ArrowLeft size={16} weight="bold" /> Back to the site
        </Link>
        <p className="font-serif text-3xl text-primary tracking-tight">
          The Timeshare <em className="text-accent">Edit</em>
        </p>
        <h1 className="text-sm font-bold uppercase tracking-[0.25em] text-muted-foreground mt-3">Admin sign in</h1>
        <form onSubmit={submit} className="mt-10 space-y-4" data-testid="admin-login-form">
          <input
            data-testid="admin-login-email"
            type="email"
            required
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm font-semibold text-destructive bg-destructive/10 border border-destructive/20 rounded-sm px-4 py-3">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
          >
            <SignIn size={17} weight="bold" />
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div className="mt-6 flex items-center gap-4" aria-hidden>
          <span className="flex-1 border-t border-border" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">or</span>
          <span className="flex-1 border-t border-border" />
        </div>
        <button
          data-testid="admin-login-google-button"
          onClick={googleSignIn}
          className="mt-6 w-full flex items-center justify-center gap-3 bg-card border border-border text-foreground px-8 py-4 rounded-full font-semibold text-sm hover:border-primary transition-colors duration-300"
        >
          <GoogleLogo size={18} weight="bold" />
          Sign in with Google
        </button>
        <p className="mt-4 text-xs text-foreground/45 text-center">Google sign-in is restricted to the site owner's account.</p>
      </motion.div>
    </div>
  );
}
